// ══════════════════════════════════════════════════════════
//  #DUMP_SITES_V1 (2026-10-26) — slurry / drilling-mud dump sites, one shared list for every DOPE map.
//
//  Bill: "Here is our Dump site — can we stitch this into our GEO TAB map and the ability to add to this list?
//  Maybe even a list we could place on Job list that will pipe into Maps? Need a shape we will not use anywhere
//  else and ability to toggle off/on for PMs. Very useful when we have equipment/people in an area looking for a site."
//
//  • Stored in Supabase app_storage key 'geo-dump-sites' (array). Seeded from Hanna's Google Earth file
//    (DUMP SITES.kmz, 36 sites, 10/23/26). Field roles can read it; office roles add / edit (Job List → Dump Sites).
//  • Map shape: an upside-down mud-brown TRIANGLE ▼ with a sand edge. Not used for anything else on any map
//    (trackers = dots, rigs = diamonds, groups = numbered circles).
//  • Toggle: "▼ Dump sites" on the GPS map, OFF until turned on, remembered per browser (localStorage 'dope_map_dumps').
//  • A unit's card on the GPS map lists the 3 nearest dump sites with miles + rate.
// ══════════════════════════════════════════════════════════
import { useEffect, useState } from 'react';

export const DUMP_KEY = 'geo-dump-sites';
export const DUMP_FILL = '#9A5B2A';    // mud brown
export const DUMP_EDGE = '#F2D3A0';    // sand
const PREF_KEY = 'dope_map_dumps_v2';   // #DUMP_SITES_OFF_RESET_V1 (2026-10-26) — new key so every browser starts OFF, whatever was saved before

const _esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

// #DUMP_SITES_OFF_DEFAULT_V1 (2026-10-26) — Bill: default OFF; a PM who wants them taps "▼ Dump sites" (remembered per browser).
export const dumpShowGet = () => { try { return window.localStorage.getItem(PREF_KEY) === '1'; } catch { return false; } };
export const dumpShowSet = (on) => { try { window.localStorage.setItem(PREF_KEY, on ? '1' : '0'); } catch { /* private window */ } try { window.dispatchEvent(new CustomEvent('dope-dumps-toggle', { detail: !!on })); } catch { /* old browser */ } };

// Live list + show/hide, shared by every map on the page (toggling one map toggles them all).
export function useDumpSites(sg) {
  const [sites, setSites] = useState(null);
  const [show, setShow] = useState(dumpShowGet);
  useEffect(() => {
    let dead = false;
    const load = async () => { if (!sg) return; try { const v = await sg(DUMP_KEY); if (!dead) setSites(cleanSites(v)); } catch { if (!dead) setSites([]); } };
    load();
    const onChg = () => load();
    const onTog = (e) => setShow(!!(e && e.detail));
    window.addEventListener('dope-dumps-changed', onChg);
    window.addEventListener('dope-dumps-toggle', onTog);
    return () => { dead = true; window.removeEventListener('dope-dumps-changed', onChg); window.removeEventListener('dope-dumps-toggle', onTog); };
  }, [sg]);
  const toggle = () => { const n = !show; setShow(n); dumpShowSet(n); };
  return { sites: sites || [], loaded: sites !== null, show, toggle };
}

export const cleanSites = (v) => (Array.isArray(v) ? v : []).filter(s => s && s.id && s.active !== false && isFinite(+s.lat) && isFinite(+s.lng) && (+s.lat !== 0 || +s.lng !== 0))
  .map(s => ({ ...s, lat: +s.lat, lng: +s.lng }));

export const milesBetween = (aLat, aLon, bLat, bLon) => {
  const R = 3958.8, r = Math.PI / 180, dLat = (bLat - aLat) * r, dLon = (bLon - aLon) * r;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(aLat * r) * Math.cos(bLat * r) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
};
export const nearestDumps = (lat, lon, sites, n = 3, maxMi = 150) => (sites || [])
  .map(s => ({ s, mi: milesBetween(lat, lon, s.lat, s.lng) })).filter(x => x.mi <= maxMi)
  .sort((a, b) => a.mi - b.mi).slice(0, n);

export const dumpMapsUrl = (s) => `https://www.google.com/maps/dir/?api=1&destination=${s.lat},${s.lng}`;

// The marker: an upside-down triangle (a "drop here" pointer), never used for anything else.
export const dumpIconHtml = (full) => {
  const w = full ? 18 : 13, h = Math.round(w * 0.9);
  return `<div style="position:absolute;left:${-w / 2}px;top:${-h}px;width:${w}px;height:${h}px;filter:drop-shadow(0 0 3px #000)">`
    + `<svg width="${w}" height="${h}" viewBox="0 0 20 18"><path d="M1.5 1.5 L18.5 1.5 L10 16.5 Z" fill="${DUMP_FILL}" stroke="${DUMP_EDGE}" stroke-width="2" stroke-linejoin="round"/></svg></div>`;
};
export const dumpPopupHtml = (s) => [
  `<div style="font-size:10px;font-weight:900;letter-spacing:.6px;color:${DUMP_EDGE};text-transform:uppercase">▼ Dump site</div>`,
  `<div style="font-size:14px;font-weight:900;color:#EDEEF0">${_esc(s.name)}</div>`,
  s.address ? `<div>${_esc(s.address)}</div>` : '',
  s.rates ? `<div style="margin-top:4px;color:#FFB020;font-weight:700">${_esc(s.rates)}</div>` : '',
  s.contact ? `<div>Contact: <b>${_esc(s.contact)}</b></div>` : '',
  s.phone ? `<div>${String(s.phone).split(/\s*,\s*/).map(p => `<a href="tel:${_esc(p.replace(/[^\d+]/g, ''))}">${_esc(p)}</a>`).join(' · ')}</div>` : '',
  s.hours ? `<div style="color:#9AA0A8">Hours: ${_esc(s.hours)}</div>` : '',
  s.notes && s.notes !== s.rates ? `<div style="margin-top:4px;color:#9AA0A8;font-size:11px;white-space:pre-wrap;max-height:110px;overflow:auto">${_esc(s.notes)}</div>` : '',
  `<div style="margin-top:6px"><a href="${_esc(dumpMapsUrl(s))}" target="_blank" rel="noopener noreferrer">Directions in Google Maps ↗</a></div>`,
].join('');
// Block for a unit's card: the nearest few dump sites.
export const nearestDumpsHtml = (lat, lon, sites) => {
  const near = nearestDumps(lat, lon, sites, 3);
  if (!near.length) return '';
  return `<div style="margin-top:6px;border-top:1px solid #3A3D42;padding-top:5px"><div style="font-size:10px;font-weight:900;letter-spacing:.5px;color:${DUMP_EDGE};text-transform:uppercase">▼ Nearest dump sites</div>`
    + near.map(({ s, mi }) => `<div style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis"><a href="${_esc(dumpMapsUrl(s))}" target="_blank" rel="noopener noreferrer"><b>${_esc(s.name)}</b></a> <span style="color:#9AA0A8">${mi < 10 ? mi.toFixed(1) : Math.round(mi)} mi${s.rates ? ' · ' + _esc(String(s.rates).split(' · ')[0]) : ''}</span></div>`).join('')
    + '</div>';
};

// ── KML / KMZ import (Google Earth "Save place as…") ──
export function parseKml(text) {
  const doc = new DOMParser().parseFromString(String(text || ''), 'application/xml');
  const out = [];
  Array.from(doc.getElementsByTagName('Placemark')).forEach((pm, i) => {
    const t = (tag) => { const el = pm.getElementsByTagName(tag)[0]; return el ? String(el.textContent || '').trim() : ''; };
    const co = t('coordinates').split(/\s+/)[0].split(',');
    const lng = parseFloat(co[0]), lat = parseFloat(co[1]);
    if (!isFinite(lat) || !isFinite(lng)) return;
    const desc = t('description').replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim();
    const lines = desc.split('\n').map(l => l.trim()).filter(Boolean);
    const phones = [...new Set(desc.match(/\(?\d{3}\)?[\s.-]?\d{3}-\d{4}/g) || [])];
    const emails = [...new Set(desc.match(/[\w.+-]+@[\w-]+\.[\w.]+/g) || [])];
    let address = '';
    const ai = lines.findIndex(l => /^\d{2,6}\s+\S/.test(l) && !/\$|gal/i.test(l));
    if (ai >= 0) { address = lines[ai]; if (lines[ai + 1] && /\bTX\b|texas/i.test(lines[ai + 1]) && !/\bTX\b|texas/i.test(address)) address += ', ' + lines[ai + 1]; }
    out.push({ id: `ds-k${Date.now().toString(36)}${i}`, name: t('name') || `Dump site ${i + 1}`, address, phone: phones.join(', '), email: emails.join(', '),
      rates: lines.filter(l => l.includes('$')).join(' · '), notes: desc, lat: +lat.toFixed(6), lng: +lng.toFixed(6), active: true });
  });
  return out;
}
// KMZ = a zip with doc.kml inside. Read the zip directory by hand and inflate with the browser's own
// DecompressionStream — no extra library.
export async function readKmlFromFile(file) {
  const name = String((file && file.name) || '').toLowerCase();
  if (name.endsWith('.kml')) return await file.text();
  const buf = new Uint8Array(await file.arrayBuffer());
  const dv = new DataView(buf.buffer);
  let eocd = -1;
  for (let i = buf.length - 22; i >= Math.max(0, buf.length - 66000); i--) { if (dv.getUint32(i, true) === 0x06054b50) { eocd = i; break; } }
  if (eocd < 0) throw new Error('not a KMZ / zip file');
  const n = dv.getUint16(eocd + 10, true); let p = dv.getUint32(eocd + 16, true);
  for (let k = 0; k < n; k++) {
    const method = dv.getUint16(p + 10, true), csize = dv.getUint32(p + 20, true);
    const fnLen = dv.getUint16(p + 28, true), exLen = dv.getUint16(p + 30, true), cmLen = dv.getUint16(p + 32, true), lho = dv.getUint32(p + 42, true);
    const fn = new TextDecoder().decode(buf.slice(p + 46, p + 46 + fnLen));
    p += 46 + fnLen + exLen + cmLen;
    if (!/\.kml$/i.test(fn)) continue;
    const start = lho + 30 + dv.getUint16(lho + 26, true) + dv.getUint16(lho + 28, true);
    const data = buf.slice(start, start + csize);
    if (method === 0) return new TextDecoder().decode(data);
    if (method !== 8 || typeof window.DecompressionStream !== 'function') throw new Error('this browser cannot open KMZ — save it as .kml in Google Earth instead');
    const ds = new window.DecompressionStream('deflate-raw');
    const out = await new Response(new Blob([data]).stream().pipeThrough(ds)).arrayBuffer();
    return new TextDecoder().decode(out);
  }
  throw new Error('no .kml inside the KMZ');
}
